import get from 'lodash/get';
import { createClient } from 'graphql-ws';
import { onError } from '@apollo/client/link/error';
import { persistCache } from 'apollo3-cache-persist';
// @ts-ignore
import { createUploadLink } from 'apollo-upload-client';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { ApolloClient, ApolloLink, from, split } from '@apollo/client';
import { InMemoryCache, defaultDataIdFromObject } from '@apollo/client/cache';
import { getMainDefinition, Observable, omitDeep } from '@apollo/client/utilities';

import packageJson from '../../package.json';
import { GraphQLError } from 'graphql';
import { transformInvisibleData } from './helpers';
import { gaSendGqlError } from 'services/googleAnalytics';
import { interceptZapierLogin } from 'services/common/loginHelper';
import { isLoadingVar, isLoggedInVar, isOnlineVar } from './cache';
import { FORBIDDEN_RESPONSE_OPERATION_NAMES } from 'services/constants';
import { authStorage, handleSignInCompleted, logout } from 'utils/helpers/auth';
import { VIBO_CLIENT_KEY, VIBO_TOKEN_KEY, VIBO_VERSION_KEY } from 'utils/constants';

import { REFRESH_TOKEN } from './mutations/auth';

const typeDefs = require('./schema.graphql');

const appVersion = packageJson.version;

let refreshTokenPromise: Promise<string> | null = null;

const goNotFoundPage = () => window?.location?.assign('/not-found');

const refreshToken = async () => {
  if (refreshTokenPromise) {
    return refreshTokenPromise;
  }

  refreshTokenPromise = new Promise(async (resolve, reject) => {
    try {
      const { data } = await client.mutate({
        mutation: REFRESH_TOKEN,
        variables: {
          refreshToken: authStorage.refreshToken,
        },
        fetchPolicy: 'no-cache',
      });

      const { accessToken, refreshToken } = data.refreshToken;

      if (!accessToken || !refreshToken) {
        return logout();
      }

      authStorage.token = accessToken;
      authStorage.refreshToken = refreshToken;
      resolve(accessToken);
    } catch (error) {
      logout();
      reject(error);
    } finally {
      refreshTokenPromise = null;
    }
  });

  return refreshTokenPromise;
};

const resolvers = {
  Mutation: {
    logUserIn: (_: unknown, response: AuthResponse) => {
      if (interceptZapierLogin(response.user._id)) {
        return null;
      }

      handleSignInCompleted(response);
      return null;
    },
  },
};

const httpLink = createUploadLink({
  uri: process.env.REACT_APP_API_URI,
});

const wsLink = new GraphQLWsLink(
  createClient({
    url: process.env.REACT_APP_WS_URI!,
  })
);

const forbiddenLink = onError(({ graphQLErrors, operation }) => {
  const { operationName } = operation;
  const code = get(graphQLErrors, '[0].code');
  const goForbiddenPage = () => window?.location?.assign('/forbidden');

  gaSendGqlError(graphQLErrors?.[0] as GraphQLError);

  isLoadingVar(false);

  if (code === 'FORBIDDEN' && FORBIDDEN_RESPONSE_OPERATION_NAMES.includes(operationName)) {
    return goForbiddenPage();
  }

  return;
});

const authMiddleware = new ApolloLink((operation, forward) => {
  operation.setContext(({ headers = {} }) => ({
    headers: {
      ...headers,
      [VIBO_CLIENT_KEY]: 'web',
      [VIBO_VERSION_KEY]: appVersion,
      ...(!!authStorage.token ? { [VIBO_TOKEN_KEY]: authStorage.token } : {}),
    },
  }));

  return forward(operation);
});

const afterwareLink = new ApolloLink((operation, forward) =>
  forward(operation).map(response => {
    const {
      response: { headers },
    } = operation.getContext();

    if (headers) {
      const token = headers.get(VIBO_TOKEN_KEY);

      if (token) {
        authStorage.token = token;
      }
    }

    return response;
  })
);

const cleanTypeName = new ApolloLink((operation, forward) => {
  if (operation.variables) {
    operation.variables = omitDeep(operation.variables, '__typename');
  }
  return forward(operation).map(data => {
    return data;
  });
});

const loadingLink = new ApolloLink((operation, forward) => {
  isLoadingVar(true);

  return forward?.(operation).map(response => {
    isLoadingVar(false);

    return response;
  });
});

const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  gaSendGqlError(graphQLErrors?.[0] as GraphQLError);

  isLoadingVar(false);

  if (operation.operationName === 'refreshToken') {
    return;
  }

  if (graphQLErrors) {
    for (let err of graphQLErrors) {
      const code = get(err, 'code');
      const pathname = window.location.pathname;

      if (code === 'UNAUTHORIZED' && pathname !== '/connect-applemusic') {
        return new Observable(observer => {
          refreshToken()
            .then(newToken => {
              const oldHeaders = operation.getContext().headers;
              operation.setContext({
                headers: {
                  ...oldHeaders,
                  [VIBO_TOKEN_KEY]: newToken,
                },
              });
              forward(operation).subscribe({
                next: observer.next.bind(observer),
                error: observer.error.bind(observer),
                complete: observer.complete.bind(observer),
              });
            })
            .catch(error => {
              observer.error(error);
            });
        });
      }

      if (code === 'CANNOT_GET_EVENT') {
        return goNotFoundPage();
      }
    }
  }

  // if (networkError) {
  //   console.error('Network error:', networkError);
  //   if (networkError.statusCode === 401) {
  //     window.location.href = '/login';
  //   }
  // }
});

const link = split(
  ({ query }) => {
    const definition = getMainDefinition(query);

    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
  },
  wsLink,
  from([
    loadingLink,
    authMiddleware,
    cleanTypeName,
    errorLink,
    forbiddenLink,
    afterwareLink,
    httpLink,
  ])
);

export const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        isLoggedIn: {
          read() {
            return isLoggedInVar();
          },
        },
      },
    },
    EventContact: transformInvisibleData,
    User: transformInvisibleData,
  },
  dataIdFromObject: (object: any) => {
    switch (object.__typename) {
      case 'SectionSong':
      case 'PrepModeSong':
      case 'Section':
      case 'TemplateSection':
      case 'FavoriteSection': {
        return object._id;
      }
      case 'PrepModeSection': {
        return `PrepModeSection:${object._id}`;
      }
      case 'SearchedSong': {
        return object.viboSongId
          ? `SearchedSong:${object.viboSongId}`
          : `SearchedSong:${object.songUrl}`;
      }
      case 'SearchedSongForSongIdeas': {
        return object.viboSongId
          ? `SearchedSongForSongIdeas:${object.viboSongId}`
          : `SearchedSongForSongIdeas:${object.songUrl}`;
      }
      case 'PlaylistSongsResponse':
      case 'EventFileTimelineOptions':
      case 'EventFileSectionsOptionsWeb':
      case 'EventFilePlaylistsOptionsWeb':
      case 'PrintSectionOptions': {
        return null;
      }
      default:
        return defaultDataIdFromObject(object);
    }
  },
  addTypename: true,
});

const client = new ApolloClient({
  link,
  cache,
  resolvers,
  typeDefs,
  connectToDevTools: process.env.NODE_ENV === 'development',
});

persistCache({
  cache,
  storage: window.localStorage,
});

window.addEventListener('load', () => {
  isOnlineVar(navigator.onLine);
});

window.addEventListener('online', () => {
  isOnlineVar(true);
});

window.addEventListener('offline', () => {
  isOnlineVar(false);
});

export default client;
